import { useMemo, useRef } from 'react';
import { Options, SelectEventObject } from 'highcharts';
import { isEqual } from 'lodash';
import { graphUtils } from '../../../utils/graph-utils';
import { useGraphContext } from '../../../GraphTheme.provider';
import { IGetOptionsProps } from '../../../types';

type UseChartOptions = {
  width: IGetOptionsProps['width'];
  height: IGetOptionsProps['height'];
  onSelection: IGetOptionsProps['onSelection'];
  events: IGetOptionsProps['events'];
  overrideChartOptions: IGetOptionsProps['overrideChartOptions'];
};

export const useChartOptions = ({ width, height, onSelection, events, overrideChartOptions }: UseChartOptions) => {
  const { handleEventMenuClick, eventMenuDataO } = useGraphContext();
  const prevDeps = useRef<Record<string, unknown> | null>(null);
  const clickEventsRef = useRef<[HTMLSpanElement, (clickEvent: PointerEvent) => void][]>([]);

  const chartOptions: Options['chart'] = useMemo(() => {
    return {
      width,
      height,
      style: {
        fontSize: '14px',
      },
      backgroundColor: 'transparent',
      ...(onSelection && {
        zooming: {
          type: 'x',
          mouseWheel: false,
        },
      }),
      events: {
        load() {
          const chartContainerMouseMove = (event: MouseEvent) => {
            const containerCoords = this.container.getBoundingClientRect();
            const relativeMouseX = event.pageX - containerCoords.x;
            const relativeMouseY = event.pageY - containerCoords.y;

            this.mouseCoords = { x: relativeMouseX, y: relativeMouseY };
          };

          this.container.addEventListener('mousemove', chartContainerMouseMove);
        },
        ...(events && {
          render() {
            const { groupedEvents, deps } = graphUtils.groupEventsByDensity(events, this);

            if (!isEqual(deps, prevDeps.current)) {
              graphUtils.renderEventPlotlines({
                chart: this,
                groupedEvents,
                onClick: handleEventMenuClick,
                menuObservable: eventMenuDataO,
              });

              prevDeps.current = deps;
            }

            graphUtils.addClickHandlerToEventPlotlineLabels({
              chart: this,
              groupedEvents,
              onClick: handleEventMenuClick,
              menuObservable: eventMenuDataO,
              clickEventsRef,
            });
          },
        }),
        ...(onSelection && {
          selection: (event: SelectEventObject): boolean => {
            const selected = event.xAxis?.[0];

            if (!selected) return false;

            onSelection(selected.min, selected.max);

            return false;
          },
        }),
      },
      spacingTop: 3,
      spacingBottom: 0,
      ...overrideChartOptions,
    };
  }, [width, height, onSelection, events, handleEventMenuClick, overrideChartOptions]);

  return chartOptions;
};
